import type { TFunction } from 'i18next';
import type { ValidationState } from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import type { LineParse } from './utils';

/** An account option for the searchable picker (postable leaves only). */
export type AccountOption = {
    id: string;
    code: string;
    name: string;
    /** `"code name"` — the combobox filters (contains) on this, so typing the
     *  code or any part of the localized name matches. */
    searchText: string;
};

/**
 * The single Post-blocked reason to surface on/near the Post button, derived
 * from the top (most-actionable) validation issue. Returns `null` when postable.
 * `i18n` keys live under `pages:journalEntry.reasons`.
 */
export const postBlockedReason = (
    state: ValidationState,
    t: TFunction,
): string | null => {
    const issue = state.issues[0];
    if (!issue) {
        return null;
    }
    switch (issue.code) {
        case 'TOO_FEW_LINES':
            return t('journalEntry.reasons.tooFewLines');
        case 'LINE_MISSING_ACCOUNT':
            return t('journalEntry.reasons.lineMissingAccount', {
                line: issue.lineIndex + 1,
            });
        case 'LINE_MISSING_AMOUNT':
            return t('journalEntry.reasons.lineMissingAmount', {
                line: issue.lineIndex + 1,
            });
        case 'LINE_TWO_SIDED':
            return t('journalEntry.reasons.lineTwoSided', {
                line: issue.lineIndex + 1,
            });
        case 'UNBALANCED':
            return t('journalEntry.reasons.unbalanced', {
                amount: formatGel(Math.abs(state.differenceTetri)),
            });
        case 'PERIOD_CLOSED':
            return t('journalEntry.reasons.periodClosed');
        case 'SERVER_REJECTED':
            // A rule the server enforces and this client does not model. Its
            // own words are the only description of it we have.
            return issue.message;
        default:
            return null;
    }
};

/**
 * Turn a write failure into something the user can act on.
 *
 * The rule from the integration brief: **where client and server disagree, the
 * server wins and the user is told why.** So a rejection the client did not
 * predict must never collapse into "Save failed".
 *
 * Order of preference:
 *
 * 1. A localized message for a code we know. Preferred because the server's
 *    prose is English-only, and half this app's users read Georgian.
 * 2. The server's own message for anything else — unhelpful in the wrong
 *    language, but true, specific, and better than a generic failure.
 * 3. The generic fallback, only when the server said nothing at all.
 *
 * The codes below were each observed from the live API; see
 * `resources/journal/CONTRACT.md`.
 */
const SERVER_ERROR_KEYS: Record<string, string> = {
    PERIOD_CLOSED: 'journalEntry.errors.periodClosed',
    UNBALANCED: 'journalEntry.errors.unbalanced',
    TOO_FEW_LINES: 'journalEntry.errors.tooFewLines',
    LINE_BOTH_SIDES: 'journalEntry.errors.lineTwoSided',
    LINE_NO_AMOUNT: 'journalEntry.errors.lineMissingAmount',
    LINE_NO_ACCOUNT: 'journalEntry.errors.lineMissingAccount',
    // Sent only if a line carried both the decimal and the tetri form of an
    // amount and they disagreed. This client sends tetri alone, so it should be
    // unreachable — if it fires, `toWriteRequest` has started sending both.
    AMOUNT_CONFLICT: 'journalEntry.errors.amountConflict',
    ACCOUNT_NOT_POSTABLE: 'journalEntry.errors.accountNotPostable',
    ACCOUNT_ARCHIVED: 'journalEntry.errors.accountArchived',
    STALE_UPDATE: 'journalEntry.errors.staleUpdate',
    // Should be unreachable: no write path can carry a `source`. If it ever
    // fires, it means one was added — say so plainly rather than blaming the
    // user's entry for a client bug.
    SOURCE_READONLY: 'journalEntry.errors.sourceReadonly',
};

type WriteError = { code?: string; message?: string };

const writeErrorMessage = (
    error: WriteError,
    t: TFunction,
    fallbackKey: string,
): string => {
    const key = error.code ? SERVER_ERROR_KEYS[error.code] : undefined;
    if (key) {
        return t(key);
    }
    return error.message || t(fallbackKey);
};

export const saveErrorMessage = (error: WriteError, t: TFunction): string =>
    writeErrorMessage(error, t, 'journalEntry.errors.saveFailed');

export const postErrorMessage = (error: WriteError, t: TFunction): string =>
    writeErrorMessage(error, t, 'journalEntry.errors.postFailed');

/** Localized per-field amount error (invalid / sub-tetri / negative), or null. */
export const amountErrorText = (
    error: LineParse['debitError'],
    t: TFunction,
): string | null => {
    if (!error) {
        return null;
    }
    return t(`journalEntry.amountErrors.${error}` as const);
};
